import { BlockStack, Button, Card, InlineStack, ProgressBar, Text } from '@shopify/polaris';
import IfCondition from 'components/shared/If-condition';
import { LoadingBar } from 'components/shared/LoadingBar';
import ResizeableSekeletonText from 'components/shared/skeletons/resizeable-skeleton-text/ResizeableSekeletonText';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface FulfilledByMonthCardProps {
    isUnlimited: boolean;
    loading: boolean;
    loadingData: boolean;
    hasSubscription: boolean;
    currentlyUsed: string;
    limit: string;
}

const FulfilledByMonthCard: React.FC<FulfilledByMonthCardProps> = ({
    isUnlimited,
    loading,
    loadingData,
    hasSubscription,
    currentlyUsed,
    limit,
}) => {
    const { t } = useTranslation();
    const { appHandle, goToPLans, isLoading } = useCurrentSubscriptionHelper();

    const progress = useMemo(() => {
        return (parseInt(currentlyUsed, 10) * 100) / parseInt(limit, 10) || 0;
    }, [limit, currentlyUsed]);

    const progressBarColor = useMemo(() => (progress < 100 ? 'highlight' : 'critical'), [progress]);

    return (
        <Card padding="400">
            <BlockStack gap={'200'}>
                <InlineStack align="space-between">
                    <BlockStack gap={'100'}>
                        {!isUnlimited ? (
                            <>
                                <Text as="p" variant="headingSm">
                                    {t('DASHBOARD.FULFILL_MONTH')}
                                </Text>

                                <IfCondition condition={loading || loadingData}>
                                    <ResizeableSekeletonText width="200px" height="20px" color="bg-fill-tertiary" />
                                    <LoadingBar />
                                </IfCondition>
                                <IfCondition condition={!loading && !loadingData}>
                                    {!hasSubscription && (
                                        <Text as="p" variant="headingMd" fontWeight="bold">
                                            {t('DASHBOARD.NO_PLAN_SELECTED')}
                                        </Text>
                                    )}
                                    <IfCondition condition={hasSubscription}>
                                        <Text as="p" variant="headingMd" fontWeight="bold">
                                            {t('DASHBOARD.ORDERS_PROGRESS', {
                                                fulfilled_orders: currentlyUsed,
                                                all_orders: limit,
                                            })}
                                        </Text>
                                    </IfCondition>
                                </IfCondition>
                            </>
                        ) : (
                            <Text as="p" variant="headingMd">
                                {t('DASHBOARD.UNLIMITED_ORDERS')}
                            </Text>
                        )}
                    </BlockStack>
                    <div>
                        <Button onClick={goToPLans} variant="primary" loading={!appHandle || isLoading}>
                            {t('DASHBOARD.CHANGE_PLAN')}
                        </Button>
                    </div>
                </InlineStack>

                {!isUnlimited && <ProgressBar size="small" progress={progress} tone={progressBarColor} />}

                {progress >= 100 && !isUnlimited && (
                    <Text as="p" fontWeight="medium">
                        {t('DASHBOARD.LIMIT_REACHED')}
                    </Text>
                )}
            </BlockStack>
        </Card>
    );
};

export default FulfilledByMonthCard;
